import { useDeferredValue, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ClipboardList, Plus } from 'lucide-react';
import {
  PERMISSIONS,
  PurchaseOrderStatus,
  SupplierActiveFilter,
  type PurchaseOrderSummary,
} from '@optiflow/types';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { useBranch } from '@/hooks/useBranch';
import { useCurrency } from '@/hooks/useCurrency';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import { usePurchaseOrdersQuery } from '../api/purchase-orders.api';
import { useSuppliersQuery } from '../api/suppliers.api';
import { PurchaseOrderStatusBadge } from '../components/PurchaseOrderStatusBadge';

const PAGE_SIZE = 20;

/**
 * What the tenant has on order, across the branches the viewer can reach.
 *
 * The branch filter only ever offers branches the signed-in user actually has, because the API
 * refuses any other — offering one and then showing an error would be the screen pretending to a
 * reach it does not have.
 */
export function PurchaseOrdersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { branches, activeBranch } = useBranch();
  const { format } = useCurrency();
  const [params] = useSearchParams();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<PurchaseOrderStatus | ''>('');
  const [supplierId, setSupplierId] = useState(params.get('supplierId') ?? '');
  const [branchId, setBranchId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  const deferredSearch = useDeferredValue(search);
  const { data, isPending, isError, error, refetch, isFetching } = usePurchaseOrdersQuery({
    page,
    pageSize: PAGE_SIZE,
    search: deferredSearch,
    status,
    supplierId,
    branchId,
    dateFrom,
    dateTo,
  });

  const suppliers = useSuppliersQuery({ pageSize: 100, active: SupplierActiveFilter.ALL });
  const canCreate = can(PERMISSIONS.PURCHASING_CREATE);

  const resetPage =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value);
      setPage(1);
    };

  const statusOptions = [
    { value: '', label: t('purchasing.orders.filters.allStatuses') },
    ...Object.values(PurchaseOrderStatus).map((value) => ({
      value,
      label: t(`purchasing.orders.status.${value}`),
    })),
  ];

  const supplierOptions = [
    { value: '', label: t('purchasing.orders.filters.allSuppliers') },
    ...(suppliers.data?.items ?? []).map((supplier) => ({
      value: supplier.id,
      label: supplier.name,
    })),
  ];

  const branchOptions = [
    { value: '', label: t('purchasing.orders.filters.allBranches') },
    ...branches.map((branch) => ({ value: branch.id, label: branch.name })),
  ];

  const columns: Column<PurchaseOrderSummary>[] = [
    {
      key: 'reference',
      header: t('purchasing.orders.fields.reference'),
      primary: true,
      render: (order) => (
        <span className="flex flex-col">
          <span className="text-ink font-mono font-medium" dir="ltr">
            {order.reference}
          </span>
          <span className="text-muted text-xs" dir="auto">
            {order.supplierName}
          </span>
        </span>
      ),
    },
    {
      key: 'branch',
      header: t('purchasing.orders.fields.branch'),
      render: (order) => <span className="text-muted text-sm">{order.branchName}</span>,
    },
    {
      key: 'status',
      header: t('inventory.fields.state'),
      render: (order) => <PurchaseOrderStatusBadge status={order.status} />,
    },
    {
      key: 'dates',
      header: t('purchasing.orders.fields.orderedOn'),
      render: (order) => (
        <span className="flex flex-col text-xs">
          <span className="text-ink" dir="ltr">
            {order.orderedOn ?? '—'}
          </span>
          {order.expectedDeliveryDate ? (
            <span className="text-muted" dir="ltr">
              {t('purchasing.orders.fields.expectedShort', {
                date: order.expectedDeliveryDate,
              })}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'quantity',
      header: t('purchasing.orders.fields.quantity'),
      align: 'end',
      render: (order) => (
        <span className="text-muted" dir="ltr">
          {order.totalQuantity}
        </span>
      ),
    },
    {
      key: 'total',
      header: t('purchasing.orders.fields.total'),
      align: 'end',
      render: (order) => (
        <span className="text-ink font-medium" dir="ltr">
          {format(order.totalMinor)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      render: (order) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void navigate(`/purchasing/orders/${order.id}`)}
          aria-label={t('purchasing.orders.openNamed', { reference: order.reference })}
        >
          {t('common.view')}
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('purchasing.orders.title')}
        subtitle={t('purchasing.orders.subtitle')}
        actions={
          canCreate ? (
            <Button onClick={() => void navigate('/purchasing/orders/new')}>
              <Plus className="size-4" aria-hidden />
              {t('purchasing.orders.create')}
            </Button>
          ) : undefined
        }
      />

      <Card>
        <CardHeader
          title={t('purchasing.orders.title')}
          subtitle={
            data ? t('purchasing.orders.count', { count: data.pagination.totalItems }) : undefined
          }
        />

        <div className="border-line grid gap-3 border-b px-4 py-3 sm:grid-cols-2 lg:grid-cols-3">
          <Input
            type="search"
            value={search}
            onChange={(event) => resetPage(setSearch)(event.target.value)}
            placeholder={t('purchasing.orders.searchPlaceholder')}
            aria-label={t('purchasing.orders.searchLabel')}
          />
          <Select
            options={statusOptions}
            value={status}
            aria-label={t('inventory.fields.state')}
            onChange={(event) =>
              resetPage(setStatus)(event.target.value as PurchaseOrderStatus | '')
            }
          />
          <Select
            options={supplierOptions}
            value={supplierId}
            aria-label={t('purchasing.orders.fields.supplier')}
            onChange={(event) => resetPage(setSupplierId)(event.target.value)}
          />
          <Select
            options={branchOptions}
            value={branchId}
            aria-label={t('purchasing.orders.fields.branch')}
            onChange={(event) => resetPage(setBranchId)(event.target.value)}
          />
          <Input
            type="date"
            value={dateFrom}
            onChange={(event) => resetPage(setDateFrom)(event.target.value)}
            aria-label={t('purchasing.orders.filters.dateFrom')}
          />
          <Input
            type="date"
            value={dateTo}
            onChange={(event) => resetPage(setDateTo)(event.target.value)}
            aria-label={t('purchasing.orders.filters.dateTo')}
          />
        </div>

        <DataTable
          caption={t('purchasing.orders.tableCaption')}
          columns={columns}
          rows={data?.items}
          rowKey={(order) => order.id}
          isLoading={isPending}
          isError={isError}
          errorMessage={apiErrorMessage(error) ?? t('purchasing.orders.loadError')}
          onRetry={() => void refetch()}
          emptyIcon={ClipboardList}
          emptyTitle={t('purchasing.orders.empty')}
          emptyDescription={
            activeBranch ? t('purchasing.orders.emptyHint') : t('purchasing.orders.selectBranch')
          }
        />

        {data ? (
          <Pagination pagination={data.pagination} onPageChange={setPage} isBusy={isFetching} />
        ) : null}
      </Card>
    </div>
  );
}
